import { useTranslation } from '../i18n';
import './ErrorFallback.scss';

/**
 * Fallback UI displayed when ErrorBoundary catches an error.
 * Provides recovery options: retry, reload, or reset data.
 * 
 * @param {Object} props
 * @param {Error} props.error - The caught error
 * @param {Object} props.errorInfo - React error info with component stack
 * @param {Function} props.reset - Function to reset error state and retry
 * @param {Function} [props.onResetData] - Function to clear all data
 */
function ErrorFallback({ error, errorInfo, reset, onResetData }) {
  const { t } = useTranslation();
  
  const handleResetData = () => {
    if (window.confirm(t('error.resetConfirm'))) {
      onResetData?.();
    }
  };

  return (
    <div className="error-fallback">
      <div className="error-fallback__icon">!</div>
      <h2 className="error-fallback__title">{t('error.title')}</h2>
      <p className="error-fallback__message">
        {t('error.message')}
      </p>
      
      <details className="error-fallback__details">
        <summary>{t('error.technicalDetails')}</summary>
        <pre className="error-fallback__error">{error?.message}</pre>
        {errorInfo?.componentStack && (
          <pre className="error-fallback__stack">{errorInfo.componentStack}</pre>
        )}
      </details>

      <div className="error-fallback__actions">
        <button 
          onClick={reset} 
          className="error-fallback__btn error-fallback__btn--primary"
        >
          {t('error.tryAgain')}
        </button>
        <button 
          onClick={() => window.location.reload()} 
          className="error-fallback__btn"
        >
          {t('error.reloadPage')}
        </button>
        {onResetData && (
          <button 
            onClick={handleResetData} 
            className="error-fallback__btn error-fallback__btn--danger"
          >
            {t('error.resetData')}
          </button>
        )}
      </div>
    </div>
  );
}

export default ErrorFallback;
